import { Component, Inject, OnInit } from '@angular/core';
import { ViolationStatement } from '../../../../models/entities/violation-statement';
import { MatButtonModule } from '@angular/material/button';
import { I18nMap } from '../../../../models/entities/i18n-map';
import { LobConsolidatedMailOptions } from '../../../../models/entities/lob-mail-options';
import { ButtonSpacerDirective } from '../../shared/directives/button.directive';
import { MobileButtonPipe } from '../../../../pipes/format/mobile-button.pipe';
import { LobMailSendRequest } from '../../../../services/lob/lob-mail-send-request';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LobService } from '../../../../services/lob/lob.service';
import { GrowlerService } from '../../../../services/growler.service';
import { SidenavContentModule } from '../../shared/sidenav/sidenav-content/sidenav-content.module';
import { SidenavContentRef } from '../../shared/sidenav/sidenav-content/sidenav-content-ref';
import { SIDENAV_CONTENT_DATA } from '../../shared/sidenav/sidenav-content/sidenav-content.service';
import { CurrencyPipe, I18nPluralPipe, NgForOf, NgIf } from '@angular/common';
import { AddressLinePipe } from '../../../../pipes/format/address-line.pipe';
import { MatDividerModule } from '@angular/material/divider';
import { ChooseMailOptionsModule } from '../../../app-layout/mail/mail-sender/choose-mail-options/choose-mail-options.module';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { InfoTooltipDirective } from '../../shared/directives/tooltip.directive';
import { finalize } from 'rxjs/operators';

export interface ViolationMailSidenavInput
{
    violations: ViolationStatement[];
    organizationId?: number;
}

export interface ViolationMailFormGroup
{
    mailClass: FormControl<string>;
    tracking: FormControl<string>;
    color: FormControl<string>;
    isDoubleSided: FormControl<boolean>;
    includeUploadedPdfForEachUnit: FormControl<boolean>;
    sendTo: FormControl<string>;

    // TODO: port over invoice handling when used in v2 layout
    doIncludeFutureInvoices: FormControl<boolean>;
    doIncludeFutureOnly: FormControl<boolean>;
    doIncludeNoBalance: FormControl<boolean>;
}

@UntilDestroy()
@Component({
    selector: 'app-violation-mail-sidenav',
    templateUrl: './violation-mail-sidenav.component.html',
    standalone: true,
    styleUrls: ['./violation-mail-sidenav.component.scss'],
    imports: [
        SidenavContentModule,
        ButtonSpacerDirective,
        MatButtonModule,
        MobileButtonPipe,
        I18nPluralPipe,
        NgIf,
        AddressLinePipe,
        MatDividerModule,
        NgForOf,
        ChooseMailOptionsModule,
        ReactiveFormsModule,
        MatSlideToggleModule,
        MatRadioModule,
        CurrencyPipe,
        InfoTooltipDirective,
    ],
})
export class ViolationMailSidenavComponent implements OnInit
{
    violations: ViolationStatement[];
    formGroup: FormGroup<ViolationMailFormGroup> = this._fb.group({
        mailClass: ['standard'],
        tracking: [{ value: '', disabled: true }],
        color: ['bw'],
        isDoubleSided: [false],
        includeUploadedPdfForEachUnit: [false],
        sendTo: ['mailing'],

        // TODO: port over invoice handling when used in v2 layout
        doIncludeFutureInvoices: [false],
        doIncludeFutureOnly: [false],
        doIncludeNoBalance: [false],
    });

    /** Mailing options, emitted from mail options component embed */
    options: LobConsolidatedMailOptions = null;

    hasMailingAddress = false;
    isSendingMail = false;

    readonly violationsMappingTerm: I18nMap = {
        '=0': 'Violations',
        '=1': 'Violation',
        other: 'Violations',
    };
    readonly ownerAddressesMappingTerm: I18nMap = {
        '=0': 'Owner mailing addresses',
        '=1': 'Owner mailing address',
        other: 'Owner mailing addresses',
    };

    readonly standardMailingPrice = 0.85;
    readonly firstClassMailingPrice = 1.05;
    readonly attachCopyEachUnitPrice = 0.20;
    readonly addColorPrice = 0.20;
    readonly certifiedMailPrice = 10;
    readonly registeredMailPrice = 25;

    constructor(
            private _lobService: LobService,
            private _fb: FormBuilder,
            private _growler: GrowlerService,
            private _sidenavRef: SidenavContentRef<ViolationMailSidenavComponent>,
            @Inject(SIDENAV_CONTENT_DATA) public data: ViolationMailSidenavInput,
    ) {
    }

    ngOnInit() {
        this.violations = this.data.violations;
        if (this.violations.length === 1) {
            for (const owner of this.violations[0]?.unit?.owners) {
                if (owner.membership?.profile?.address1) {
                    this.hasMailingAddress = true;
                }
            }
        }

        this.formGroup.get('mailClass').valueChanges.subscribe({
            next: (value) => {
                if (value === 'standard') {
                    this.formGroup.get('tracking').reset('');
                    this.formGroup.get('tracking').disable();
                } else {
                    this.formGroup.get('tracking').enable();
                }
            },
        });
    }

    mailViolation() {
        if (this.formGroup.invalid) {
            this._growler.oops('Unable to send mailing');
            return;
        }

        // TODO: fix class initialization and usage in legacy ui, then update here
        const options = new LobConsolidatedMailOptions(this.formGroup.getRawValue() as LobConsolidatedMailOptions);

        const request: LobMailSendRequest = new LobMailSendRequest({
            unitIds: [],
            ownerIds: [],
            violationIds: this.violations.map(v => v.id),
            doIncludeInvoices: false,
            options,
        });

        this._sidenavRef.loading = this.isSendingMail = true;
        this._lobService.sendMailedViolations(request, this.data.organizationId).pipe(
                finalize(() => this._sidenavRef.loading = this.isSendingMail = false),
                untilDestroyed(this),
        ).subscribe({
            next: () => {
                this._growler.success('Success', 'Violation processing to be mailed.');
                this._sidenavRef.close();
            },
            error: () => {
                this._growler.oops('Unable to mail via USPS');
            }
        });
    }
}
